import api from './api';

/**
 * Application Service
 *
 * API client for application-aware routing - Application Definitions & Groups
 * Phase 4.2: UI Integration
 */

// Application Definition types
export interface ApplicationDefinition {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;

  // Application Characteristics
  category?: 'video' | 'voip' | 'database' | 'web' | 'email' | 'file_transfer' | 'custom' | 'network' | 'remote';
  protocol?: 'tcp' | 'udp' | 'icmp' | 'any';
  destination_ports?: string;
  source_ports?: string;
  dscp_values?: string;
  ip_protocol?: number;

  // Detection & Classification
  detection_method?: 'port' | 'domain' | 'ip_range' | 'dpi' | 'mixed';
  domain_patterns?: string; // Comma-separated domain patterns for DNS/TLS detection

  // Requirements
  business_criticality?: 'critical' | 'high' | 'medium' | 'low';
  bandwidth_requirement?: number;
  latency_requirement?: number;
  jitter_requirement?: number;
  packet_loss_tolerance?: string;

  enabled: number;
  create_time?: string;
  update_time?: string;

  // Populated from joins
  groups?: ApplicationGroup[];
}

export interface ApplicationGroup {
  id: string;
  tenant_id: string;
  name: string;
  description?: string;
  enabled: number;
  create_time?: string;
  update_time?: string;

  // Populated from joins
  applications?: ApplicationDefinition[];
  memberCount?: number;
}

export interface ApplicationListParams {
  search?: string;
  category?: string;
  tenantId?: string;
  page?: number;
  pageSize?: number;
}

export interface ApplicationCreateData {
  tenant_id?: string;
  name: string;
  description?: string;
  category?: string;
  protocol?: string;
  destination_ports?: string;
  source_ports?: string;
  dscp_values?: string;
  ip_protocol?: number;
  business_criticality?: string;
  bandwidth_requirement?: number;
  latency_requirement?: number;
  jitter_requirement?: number;
  packet_loss_tolerance?: string;
  enabled?: number;
}

export interface ApplicationUpdateData {
  name?: string;
  description?: string;
  category?: string;
  protocol?: string;
  destination_ports?: string;
  source_ports?: string;
  dscp_values?: string;
  ip_protocol?: number;
  business_criticality?: string;
  bandwidth_requirement?: number;
  latency_requirement?: number;
  jitter_requirement?: number;
  packet_loss_tolerance?: string;
  enabled?: number;
}

export interface CloneTemplateData {
  templateId: string;
  tenant_id?: string;
  name?: string;
  description?: string;
}

export interface GroupCreateData {
  tenant_id?: string;
  name: string;
  description?: string;
  applicationIds?: string[];
  enabled?: number;
}

export interface PaginatedApplicationResponse {
  items: ApplicationDefinition[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

export interface PaginatedGroupResponse {
  items: ApplicationGroup[];
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

const applicationService = {
  /**
   * List all applications (auto-filtered by tenant + templates)
   */
  list: async (params?: ApplicationListParams) => {
    const response = await api.get('/applications', { params });
    return response.data as PaginatedApplicationResponse;
  },

  /**
   * Get application by ID
   */
  getById: async (id: string) => {
    const response = await api.get(`/applications/${id}`);
    return response.data as ApplicationDefinition;
  },

  /**
   * Create new application
   */
  create: async (data: ApplicationCreateData) => {
    const response = await api.post('/applications', data);
    return response.data as ApplicationDefinition;
  },

  /**
   * Update application
   */
  update: async (id: string, data: ApplicationUpdateData) => {
    const response = await api.put(`/applications/${id}`, data);
    return response.data as ApplicationDefinition;
  },

  /**
   * Delete application (soft delete)
   */
  delete: async (id: string) => {
    await api.delete(`/applications/${id}`);
  },

  /**
   * Clone template application
   */
  cloneTemplate: async (data: CloneTemplateData) => {
    const response = await api.post('/applications/clone', data);
    return response.data as ApplicationDefinition;
  },

  /**
   * List application groups
   */
  listGroups: async (params?: ApplicationListParams) => {
    const response = await api.get('/applications/groups/list', { params });
    return response.data as PaginatedGroupResponse;
  },

  /**
   * Create application group
   */
  createGroup: async (data: GroupCreateData) => {
    const response = await api.post('/applications/groups', data);
    return response.data as ApplicationGroup;
  },

  /**
   * Add application to group
   */
  addToGroup: async (groupId: string, applicationId: string) => {
    await api.post(`/applications/groups/${groupId}/members`, { applicationId });
  },

  /**
   * Remove application from group
   */
  removeFromGroup: async (groupId: string, applicationId: string) => {
    await api.delete(`/applications/groups/${groupId}/members/${applicationId}`);
  },

  /**
   * Reconcile tenant with system defaults (add missing apps, groups, policies)
   */
  reconcileDefaults: async (tenantId?: string): Promise<{
    appsAdded: number;
    groupsAdded: number;
    membersAdded: number;
    policiesAdded: number;
  }> => {
    const params = tenantId ? { tenantId } : {};
    const response = await api.post('/applications/reconcile', null, { params });
    return response.data;
  },

  /**
   * Auto-generate DSCP values for all tenant applications based on business_criticality.
   * Maps: critical→46/EF, high→34/AF41, medium→18/AF21, low→0/BE
   */
  autoGenerateDscp: async (options?: { overwrite?: boolean; tenantId?: string }): Promise<{
    generated: number;
    skipped: number;
    details: { name: string; dscp: number; phb: string }[];
  }> => {
    const params = options?.tenantId ? { tenantId: options.tenantId } : {};
    const response = await api.post('/applications/auto-generate-dscp', { overwrite: options?.overwrite ?? false }, { params });
    return response.data;
  },
};

export default applicationService;
